import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as ShippingIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface InventoryItem {
  id: string;
  partNumber: string;
  name: string;
  vehicle: {
    make: string;
    model: string;
    year: number;
  };
  category: string;
  currentStock: number;
  minimumStock: number;
  reorderPoint: number;
  unitPrice: number;
  supplier: string;
  location: string;
  lastRestocked: Date;
  status: 'in_stock' | 'low_stock' | 'out_of_stock' | 'on_order';
}

interface AdvancedFilters {
  dateRange: {
    start: string;
    end: string;
  };
  suppliers: string[];
  vehicleMakes: string[];
  priceRange: {
    min: number;
    max: number;
  };
  locations: string[];
}

interface NewInventoryItem {
  partNumber: string;
  name: string;
  vehicle: {
    make: string;
    model: string;
    year: number;
  };
  category: string;
  currentStock: number;
  minimumStock: number;
  reorderPoint: number;
  unitPrice: number;
  supplier: string;
  location: string;
}

export const InventoryStatus: React.FC = () => {
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAdvancedFiltersDialog, setOpenAdvancedFiltersDialog] = useState(false);
  const [openNewItemDialog, setOpenNewItemDialog] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    dateRange: {
      start: '',
      end: '',
    },
    suppliers: [],
    vehicleMakes: [],
    priceRange: {
      min: 0,
      max: 1000000,
    },
    locations: [],
  });
  const [newItem, setNewItem] = useState<NewInventoryItem>({
    partNumber: '',
    name: '',
    vehicle: {
      make: '',
      model: '',
      year: new Date().getFullYear(),
    },
    category: '',
    currentStock: 0,
    minimumStock: 0,
    reorderPoint: 0,
    unitPrice: 0,
    supplier: '',
    location: '',
  });

  // Mock inventory data for luxury vehicles
  const inventory: InventoryItem[] = [
    {
      id: 'INV001',
      partNumber: 'FER-488-BP-24',
      name: 'Carbon Ceramic Brake System',
      vehicle: {
        make: 'Ferrari',
        model: '488 Pista',
        year: 2024,
      },
      category: 'Braking System',
      currentStock: 2,
      minimumStock: 1,
      reorderPoint: 2,
      unitPrice: 212750,
      supplier: 'Ferrari South Africa',
      location: 'High-Value Storage A1',
      lastRestocked: new Date(2024, 2, 10),
      status: 'low_stock',
    },
    {
      id: 'INV002',
      partNumber: 'MCL-765LT-ECU',
      name: 'Engine Control Unit',
      vehicle: {
        make: 'McLaren',
        model: '765LT',
        year: 2023,
      },
      category: 'Electronics',
      currentStock: 0,
      minimumStock: 1,
      reorderPoint: 1,
      unitPrice: 245000,
      supplier: 'McLaren South Africa',
      location: 'Electronics Storage B2',
      lastRestocked: new Date(2024, 2, 1),
      status: 'on_order',
    },
    {
      id: 'INV003',
      partNumber: 'POR-GT3RS-AW',
      name: 'Aerodynamic Wing Kit',
      vehicle: {
        make: 'Porsche',
        model: '911 GT3 RS',
        year: 2024,
      },
      category: 'Body & Exterior',
      currentStock: 3,
      minimumStock: 1,
      reorderPoint: 2,
      unitPrice: 385000,
      supplier: 'Porsche Centre SA',
      location: 'Large Parts Storage C3',
      lastRestocked: new Date(2024, 2, 15),
      status: 'in_stock',
    },
    {
      id: 'INV004',
      partNumber: 'LAM-SVJ-EXH',
      name: 'Titanium Exhaust System',
      vehicle: {
        make: 'Lamborghini',
        model: 'Aventador SVJ',
        year: 2022,
      },
      category: 'Exhaust System',
      currentStock: 0,
      minimumStock: 1,
      reorderPoint: 1,
      unitPrice: 875000,
      supplier: 'Lamborghini Johannesburg',
      location: 'Large Parts Storage C4',
      lastRestocked: new Date(2024, 1, 15),
      status: 'out_of_stock',
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_stock':
        return 'success';
      case 'low_stock':
        return 'warning';
      case 'out_of_stock':
        return 'error';
      case 'on_order':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_stock':
        return 'In Stock';
      case 'low_stock':
        return 'Low Stock';
      case 'out_of_stock':
        return 'Out of Stock';
      case 'on_order':
        return 'On Order';
      default:
        return status;
    }
  };

  const formatCurrency = (amount: number) => {
    return `R ${amount.toLocaleString()}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const calculateTotalValue = () => {
    return inventory.reduce((sum, item) => sum + (item.currentStock * item.unitPrice), 0);
  };

  const calculateLowStockItems = () => {
    return inventory.filter(item => item.status === 'low_stock').length;
  };

  const calculateOutOfStockItems = () => {
    return inventory.filter(item => item.status === 'out_of_stock').length;
  };

  const handleViewItem = (item: InventoryItem) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleAdvancedFiltersClick = () => {
    setOpenAdvancedFiltersDialog(true);
  };

  const handleApplyAdvancedFilters = () => {
    // Here you would typically filter the inventory based on the selected filters
    setOpenAdvancedFiltersDialog(false);
  };

  const handleAddNewItemClick = () => {
    setOpenNewItemDialog(true);
  };

  const handleNewItemSubmit = () => {
    // Here you would typically make an API call to create the item
    setOpenNewItemDialog(false);
    // Reset form
    setNewItem({
      partNumber: '',
      name: '',
      vehicle: {
        make: '',
        model: '',
        year: new Date().getFullYear(),
      },
      category: '',
      currentStock: 0,
      minimumStock: 0,
      reorderPoint: 0,
      unitPrice: 0,
      supplier: '',
      location: '',
    });
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Inventory Status
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Total Inventory Value
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(calculateTotalValue())}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {inventory.length} unique parts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="warning.main" noWrap>
                  Low Stock Items
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {calculateLowStockItems()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Below reorder point
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="error.main" noWrap>
                  Out of Stock
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {calculateOutOfStockItems()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Requires immediate attention
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="info.main" noWrap>
                  On Order
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {inventory.filter(item => item.status === 'on_order').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending delivery
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategory}
                  label="Category"
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="braking">Braking System</MenuItem>
                  <MenuItem value="electronics">Electronics</MenuItem>
                  <MenuItem value="body">Body & Exterior</MenuItem>
                  <MenuItem value="exhaust">Exhaust System</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="in_stock">In Stock</MenuItem>
                  <MenuItem value="low_stock">Low Stock</MenuItem>
                  <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                  <MenuItem value="on_order">On Order</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={handleAdvancedFiltersClick}
                >
                  Advanced Filters
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddNewItemClick}
                >
                  Add New Item
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Part Details</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Stock Level</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Total Value</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{item.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.partNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.vehicle.make} {item.vehicle.model}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.vehicle.year}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.currentStock} / {item.minimumStock}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reorder at: {item.reorderPoint}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(item.unitPrice)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(item.currentStock * item.unitPrice)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(item.status)}
                      color={getStatusColor(item.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.location}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last restocked: {formatDate(item.lastRestocked)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewItem(item)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small">
                      <HistoryIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Inventory Item Details
          </DialogTitle>
          <DialogContent>
            {selectedItem && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  {selectedItem.status === 'low_stock' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      This item is below the reorder point. Consider placing an order soon.
                    </Alert>
                  )}
                  {selectedItem.status === 'out_of_stock' && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      This item is out of stock. Immediate reorder recommended.
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Part Number"
                    value={selectedItem.partNumber}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={selectedItem.name}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Current Stock"
                    type="number"
                    value={selectedItem.currentStock}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Minimum Stock"
                    type="number"
                    value={selectedItem.minimumStock}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Reorder Point"
                    type="number"
                    value={selectedItem.reorderPoint}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Unit Price"
                    value={formatCurrency(selectedItem.unitPrice)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Total Value"
                    value={formatCurrency(selectedItem.currentStock * selectedItem.unitPrice)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={selectedItem.location}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button variant="contained" color="primary">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Advanced Filters Dialog */}
        <Dialog
          open={openAdvancedFiltersDialog}
          onClose={() => setOpenAdvancedFiltersDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Advanced Filters</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={advancedFilters.dateRange.start}
                  onChange={(e) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      dateRange: { ...advancedFilters.dateRange, start: e.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={advancedFilters.dateRange.end}
                  onChange={(e) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      dateRange: { ...advancedFilters.dateRange, end: e.target.value },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Suppliers</InputLabel>
                  <Select
                    multiple
                    value={advancedFilters.suppliers}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      setAdvancedFilters({
                        ...advancedFilters,
                        suppliers: value,
                      });
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Ferrari South Africa">Ferrari South Africa</MenuItem>
                    <MenuItem value="McLaren Johannesburg">McLaren Johannesburg</MenuItem>
                    <MenuItem value="Porsche Centre SA">Porsche Centre SA</MenuItem>
                    <MenuItem value="Lamborghini Johannesburg">Lamborghini Johannesburg</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Vehicle Makes</InputLabel>
                  <Select
                    multiple
                    value={advancedFilters.vehicleMakes}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      setAdvancedFilters({
                        ...advancedFilters,
                        vehicleMakes: value,
                      });
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Ferrari">Ferrari</MenuItem>
                    <MenuItem value="McLaren">McLaren</MenuItem>
                    <MenuItem value="Porsche">Porsche</MenuItem>
                    <MenuItem value="Lamborghini">Lamborghini</MenuItem>
                    <MenuItem value="Aston Martin">Aston Martin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Min Price"
                  type="number"
                  value={advancedFilters.priceRange.min}
                  onChange={(e) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      priceRange: {
                        ...advancedFilters.priceRange,
                        min: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Price"
                  type="number"
                  value={advancedFilters.priceRange.max}
                  onChange={(e) =>
                    setAdvancedFilters({
                      ...advancedFilters,
                      priceRange: {
                        ...advancedFilters.priceRange,
                        max: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Locations</InputLabel>
                  <Select
                    multiple
                    value={advancedFilters.locations}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      setAdvancedFilters({
                        ...advancedFilters,
                        locations: value,
                      });
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="High-Value Storage A1">High-Value Storage A1</MenuItem>
                    <MenuItem value="Electronics Storage B2">Electronics Storage B2</MenuItem>
                    <MenuItem value="Large Parts Storage C3">Large Parts Storage C3</MenuItem>
                    <MenuItem value="Large Parts Storage C4">Large Parts Storage C4</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAdvancedFiltersDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleApplyAdvancedFilters}>
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Item Dialog */}
        <Dialog
          open={openNewItemDialog}
          onClose={() => setOpenNewItemDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Add New Inventory Item</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Part Number"
                  value={newItem.partNumber}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      partNumber: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={newItem.name}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      name: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Vehicle Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Make"
                  value={newItem.vehicle.make}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      vehicle: {
                        ...newItem.vehicle,
                        make: e.target.value,
                      },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Model"
                  value={newItem.vehicle.model}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      vehicle: {
                        ...newItem.vehicle,
                        model: e.target.value,
                      },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  value={newItem.vehicle.year}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      vehicle: {
                        ...newItem.vehicle,
                        year: parseInt(e.target.value) || new Date().getFullYear(),
                      },
                    })
                  }
                  InputProps={{ inputProps: { min: 1900, max: new Date().getFullYear() + 1 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newItem.category}
                    label="Category"
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        category: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="Braking System">Braking System</MenuItem>
                    <MenuItem value="Electronics">Electronics</MenuItem>
                    <MenuItem value="Body & Exterior">Body & Exterior</MenuItem>
                    <MenuItem value="Exhaust System">Exhaust System</MenuItem>
                    <MenuItem value="Engine Components">Engine Components</MenuItem>
                    <MenuItem value="Transmission">Transmission</MenuItem>
                    <MenuItem value="Interior">Interior</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Current Stock"
                  type="number"
                  value={newItem.currentStock}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      currentStock: parseInt(e.target.value) || 0,
                    })
                  }
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Minimum Stock"
                  type="number"
                  value={newItem.minimumStock}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      minimumStock: parseInt(e.target.value) || 0,
                    })
                  }
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Reorder Point"
                  type="number"
                  value={newItem.reorderPoint}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      reorderPoint: parseInt(e.target.value) || 0,
                    })
                  }
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Unit Price"
                  type="number"
                  value={newItem.unitPrice}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      unitPrice: parseFloat(e.target.value) || 0,
                    })
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R</InputAdornment>,
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Supplier</InputLabel>
                  <Select
                    value={newItem.supplier}
                    label="Supplier"
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        supplier: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="Ferrari South Africa">Ferrari South Africa</MenuItem>
                    <MenuItem value="McLaren Johannesburg">McLaren Johannesburg</MenuItem>
                    <MenuItem value="Porsche Centre SA">Porsche Centre SA</MenuItem>
                    <MenuItem value="Lamborghini Johannesburg">Lamborghini Johannesburg</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={newItem.location}
                    label="Location"
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        location: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="High-Value Storage A1">High-Value Storage A1</MenuItem>
                    <MenuItem value="Electronics Storage B2">Electronics Storage B2</MenuItem>
                    <MenuItem value="Large Parts Storage C3">Large Parts Storage C3</MenuItem>
                    <MenuItem value="Large Parts Storage C4">Large Parts Storage C4</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNewItemDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleNewItemSubmit}
              disabled={
                !newItem.partNumber ||
                !newItem.name ||
                !newItem.vehicle.make ||
                !newItem.vehicle.model ||
                !newItem.category ||
                !newItem.supplier ||
                !newItem.location
              }
            >
              Add Item
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 